import { isEmpty, values, intersectionWith, isEqual } from 'lodash/fp';
import { DateTime } from 'luxon';
import type { ReduxActionOutcome, ReduxApiAction } from 'app/store/storeTypes';
import {
    type Accessible,
    type DeploymentDetails,
    NOT_ACCESSIBLE,
    type SnowflakeTableFqn,
    VALIDATE_SNOWFLAKE_ACCESS
} from 'app/lib/types/server';
import type { ValidateSnowflakeAccessTypeMap } from 'app/lib/types/cqrs';
import { DATA_SOURCE_REFERENCE_DTO_TYPE } from 'app/store/__generated__/types';
import { doInvalidateDownstreamDatasets } from 'app/store/datasetData/actions.js';
import { selectEntitiesById } from 'app/store/entities/selectors.js';
import { doSyncProject } from 'app/store/projects/actions.js';
import { isProject } from 'app/routes/routes.js';
import {
    selectUpstreamDataSourceFqnsByViewUid,
    selectProject
} from 'app/store/projects/selectors.js';
import { selectAllFailedDataSourcesFqns } from './selectors';
import { VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE } from './types.js';

export const doValidateSnowflakeAccess = (
    objects: SnowflakeTableFqn[],
    projectErrorNodeUids = [],
    showNotification = false
) =>
    ({
        type: VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE,
        api: { version: 2, name: VALIDATE_SNOWFLAKE_ACCESS },
        params: { _kind: VALIDATE_SNOWFLAKE_ACCESS, objects },
        meta: { projectErrorNodeUids, showNotification },
        onSucceeded: ({ dispatch, getState, payload }) => {
            if (!isProject()) {
                return;
            }

            const state = getState();
            const failedSourcesFqns = selectAllFailedDataSourcesFqns(state);
            const upstreamSourcesByFqns = selectUpstreamDataSourceFqnsByViewUid(state);
            const project = selectProject(state);
            const projectId = project?.['entityId'] ?? '';
            const projectCompiledAt = project?.['compiledAt'] ?? '';

            const projectTime = DateTime.fromISO(projectCompiledAt);
            const outOfSyncTables = payload.tables
                .filter<Accessible | DeploymentDetails>((table) => table._kind !== NOT_ACCESSIBLE)
                .filter((table) => DateTime.fromISO(table.lastDDL) > projectTime);

            if (!isEmpty(outOfSyncTables)) {
                dispatch(
                    doSyncProject(
                        outOfSyncTables.map(({ database, schema, table }) => ({
                            database,
                            schema,
                            table
                        }))
                    )
                );
            }

            for (const viewUid in upstreamSourcesByFqns) {
                const upstreamSourceForView = values(upstreamSourcesByFqns[viewUid]);
                if (!isEmpty(intersectionWith(isEqual, failedSourcesFqns, upstreamSourceForView))) {
                    const entity = selectEntitiesById(state)[`${projectId}.${viewUid}`];

                    if (entity?._type === DATA_SOURCE_REFERENCE_DTO_TYPE) {
                        dispatch(doInvalidateDownstreamDatasets({ ids: [viewUid] }));
                    }
                }
            }
        }
    }) as const satisfies ReduxApiAction<ValidateSnowflakeAccessTypeMap>;

export type AccessActionOutcomes = ReduxActionOutcome<
    ReturnType<typeof doValidateSnowflakeAccess>,
    ValidateSnowflakeAccessTypeMap
>;
